<template>
  <div class="scheme">
    <div class="upload">
      <div class="container">
        <div class="scheme-info">
          <p>
            {{ parseTranslation(HOME_TITLE) }} >
            <span>{{ parseTranslation(SCHEME_SUGGESTER) }}</span>
          </p>
          <div>
            <h1>{{ parseTranslation(SCHEME_SUGGESTER) }}</h1>
            <p>{{ parseTranslation(SUB_TITLE) }}</p>
          </div>
          <router-link to="/" class="all-surveys">{{
            parseTranslation(ALL_SURVEYS)
          }}</router-link>
        </div>
      </div>
      <div class="container">
        <div class="scheme-header">
          <div class="title">
            <h2>{{ fileName }}</h2>
            <button id="rewind" @click="rewind">
              {{ parseTranslation(BACK) }}
            </button>
          </div>
          <div>
            <div class="scheme-btns">
              <button id="save" @click.prevent="saveSurvey">
                {{ parseTranslation(SAVE) }}
              </button>
              <button @click="testSurvey">
                {{ parseTranslation(TEST) }}
              </button>
              <button id="upload-questions" @click="goToQuestionsTheme">
                {{ parseTranslation(CUSTOMIZE) }}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="container buttons">
        <button @click="addNewGroup">{{ parseTranslation(NEW_GROUP) }}</button>
        <button @click="addTemplate">{{ parseTranslation(ADD_TEMPLATE) }}</button>
        <button @click="openSurveyAdvancedSettingsModal">
          {{ parseTranslation(SURVEY_SETTINGS) }}
        </button>
      </div>
    </div>
    <div v-if="!loader">
      <Draggable
        v-if="questions_groups"
        v-model="questions_groups"
        :handle="`.${draggableClass}`"
        @start="drag = true"
        @end="drag = false"
      >
        <div
          v-for="(group, index) in questions_groups"
          :key="`${group.group_code}-index`"
          :ref="`group-${index}`"
        >
          <Questions
            :groupIndex="index"
            :questionGroups="questions_groups"
            :saveSurvey="saveSurvey"
            :draggable_class="draggableClass"
          />
        </div>
      </Draggable>
    </div>

    <Loader v-else />
  </div>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import { mapActions, mapState } from 'vuex'
import Draggable from 'vuedraggable'

import Loader from '@/components/loading/Loader'

import Questions from './Questions'

import { QUESTION_TYPES, SURVEY_TYPE } from '@/utils/constants'
import { earlyExitReduce, getFirstItem, getLastIndex } from '@/utils/array'
import { questionsUploadValidations } from '@/utils/questionsUploadValidations'
import {
  parseQuestionsBeforeSaving,
  parseQuestionsOnLoad,
} from '@/utils/uploadLoadSurveyParsers'

import * as translationScheme from './index.translations'

const { default: translations, ...translationKeys } = translationScheme

export default {
  name: 'scheme',

  components: {
    Questions,
    Draggable,
    Loader,
  },

  data() {
    return {
      translations,
      ...translationKeys,
      questionTypes: QUESTION_TYPES,
      questions: [],
      categories: {},
      loader: false,
      resp: false,
      info: null,
      draggableClass: 'handle',
    }
  },

  computed: {
    ...mapState(['questionGroups', 'groupNameChange', 'questionCodeValidation']),

    fileName() {
      return this.$store.getters.survey.name
    },

    loginModalIsOpen() {
      return this.$store.getters.loginModal.open
    },

    survey() {
      return this.$store.getters.survey
    },

    questions_groups: {
      get() {
        return this.$store.state.questionGroups
      },
      set(questions_groups) {
        this.updateSurveyGroup({
          questions_groups,
        })
      },
    },
  },

  methods: {
    ...mapActions([
      'surveyRegister',
      'sendMessage',
      'modalBtns',
      'modalPreview',
      'executeSurveyAPICall',
      'setQuestionGroups',
      'toggleLoginModal',
      'updateSurveyGroup',
      'toggleSurveyAdvancedSettingsModal',
    ]),

    getId: uuidv4,

    openSurveyAdvancedSettingsModal() {
      this.toggleSurveyAdvancedSettingsModal({ open: true })
    },

    async goToQuestionsTheme() {
      const validationError = this.validateSurvey(questionsUploadValidations)

      if (validationError) return this.sendMessage(validationError)

      if (this.questionCodeValidation) {
        return this.sendMessage(this.parseTranslation(this.QUESTION_CODE_ERROR))
      }

      if (this.questionGroupsIsNotEmpty()) return

      this.curateAdvancedSettings()

      this.curateGroupAdvancedSettings()

      this.removeRandomWhenQuestionIsInherited()

      const updatedSurvey = await this.executeSurveyAPICall({
        path: 'survey',
        method: 'PUT',
        payload: {
          survey_id: this.$store.getters.survey.id_,
          questions_groups: this.questionGroups,
        },
      })

      if (!updatedSurvey) return

      this.surveyRegister(updatedSurvey)
      this.setQuestionGroups(updatedSurvey.questions_groups)
      await this.$router.push('/theme')
    },

    testSurvey() {
      const surveyId = this.survey.id_

      const surveyType = this.survey.survey_type

      let questionsTestLink = `${this.owQuestionsTestLink}${surveyId}`

      const surveyTypeIsNotCAWI = [SURVEY_TYPE.CATI, SURVEY_TYPE.CAPI].includes(
        surveyType,
      )

      if (surveyTypeIsNotCAWI) {
        questionsTestLink = `${questionsTestLink}?interview=y`
      }

      window.open(questionsTestLink, '_blank')
    },

    async loadSurvey() {
      this.toggleLoader(true)

      const survey = await this.executeSurveyAPICall({
        path: 'survey',
        queryParams: { survey_id: this.$store.getters.survey.id_ },
      })

      if (!survey) return this.toggleLoader(false)

      this.toggleLoader(false)

      const parsedSurvey = {
        ...survey,
        questions_groups: parseQuestionsOnLoad(survey.questions_groups),
      }

      this.surveyRegister(parsedSurvey)
      this.setQuestionGroups(parsedSurvey.questions_groups)

      this.categories = {
        surveyName: survey.name,
        lang: survey.lang,
        type: survey.survey_type,
        sid: survey.id_,
      }

      const editor = JSON.parse(sessionStorage.getItem('editor'))

      if (editor) window.sessionStorage.setItem('editor', false)
    },

    async rewind() {
      if (!this.survey.text) return this.$router.push('/')

      const updatedSurvey = await this.executeSurveyAPICall({
        path: 'survey',
        method: 'PUT',
        payload: {
          survey_id: this.$store.getters.survey.id_,
          step: 'editor',
          text: this.$store.getters.survey.text,
        },
      })

      if (!updatedSurvey) return

      this.surveyRegister(updatedSurvey)
      await this.$router.push('/editor')
    },

    executeSingleValidator(questions, validator) {
      const questionError = earlyExitReduce(
        questions,
        (_acc, question) => validator(question),
        error => !!error,
        '',
      )

      return questionError
    },

    validateQuestions(questions, uploadValidations) {
      const { singleQuestionValidations } = uploadValidations

      const validators = Object.values(singleQuestionValidations)

      const error = earlyExitReduce(
        validators,
        (_acc, validator) => this.executeSingleValidator(questions, validator),
        existingError => !!existingError,
        '',
      )

      return error
    },

    validateSurvey(uploadValidations) {
      const { surveyValidations } = uploadValidations

      const surveyError = earlyExitReduce(
        Object.values(surveyValidations),
        (_acc, validator) => validator(this.survey),
        existingError => !!existingError,
        '',
      )

      if (surveyError) return surveyError

      const allQuestions = this.questionGroups.map(({ questions }) => questions).flat()

      const questionError = this.validateQuestions(allQuestions, uploadValidations)

      return questionError
    },

    toggleLoader(newValue) {
      this.loader = newValue === undefined ? !this.loader : newValue
    },

    update(questions) {
      this.questions = questions
    },

    questionGroupsIsNotEmpty() {
      const isEmpty = this.questions_groups.find(group => !group.questions.length)

      if (isEmpty) {
        this.sendMessage(this.parseTranslation(this.EMPTY_QUESTION_GROUP))
      }

      return !!isEmpty
    },

    async saveSurvey() {
      const validationError = this.validateSurvey(questionsUploadValidations)

      if (validationError) return this.sendMessage(validationError[this.language])

      if (this.questionCodeValidation) {
        return this.sendMessage(this.parseTranslation(this.QUESTION_CODE_ERROR))
      }

      if (this.questionGroupsIsNotEmpty()) return

      this.toggleLoader(true)

      this.curateAdvancedSettings()

      this.curateGroupAdvancedSettings()

      this.removeRandomWhenQuestionIsInherited()

      const advancedOptions = { ...this.survey?.advancedOptions }

      let payload = {
        questions_groups: parseQuestionsBeforeSaving(this.questionGroups),
        survey_id: this.$store.getters.survey.id_,
      }

      if (Object.keys(advancedOptions).length) {
        payload = {
          ...payload,
          advancedOptions,
        }
      }

      const savedSurvey = await this.executeSurveyAPICall({
        path: 'survey',
        method: 'PUT',
        payload,
      })

      if (!savedSurvey) return this.toggleLoader(false)

      const parsedSurvey = {
        ...savedSurvey,
        questions_groups: parseQuestionsOnLoad(savedSurvey.questions_groups),
      }

      this.surveyRegister(parsedSurvey)
      this.setQuestionGroups(parsedSurvey.questions_groups)
      this.sendMessage(this.translations[this.language][this.SURVEY_SAVED_SUCCESS])

      this.toggleLoader(false)
    },

    removeRandomWhenQuestionIsInherited() {
      const groups = this.questionGroups.map(group => ({
        ...group,

        questions: group.questions.map(question => {
          if (
            !question.advancedOptions?.array_filter &&
            !question.advancedOptions?.array_filter_reverse
          ) {
            return { ...question }
          }

          const { random_order: _, ...advancedOptions } = question.advancedOptions

          return {
            ...question,
            advancedOptions,
          }
        }),
      }))

      this.setQuestionGroups(groups)
    },

    preview(code) {
      this.modalPreview({
        url: `${this.owQuestions}${this.$store.getters.survey.id_}?questioncode=${code}`,
      })
    },

    getDefaultGroup() {
      const totalQuestionGroups = this.questions_groups.length + 1

      return {
        group_code: this.getId(),
        group_name: `Group name ${totalQuestionGroups}`,
        questions: [],
        advancedOptions: {},
        logic: {
          default: 'finish',
          conditions: [],
        },
      }
    },

    async addNewGroup() {
      const updatedGroups = [...this.questionGroups, this.getDefaultGroup()]

      this.updateSurveyGroup({
        questions_groups: updatedGroups,
      })

      this.scrollToLastGroup()

      this.sendMessage(this.translations[this.language][this.GROUP_ADDED])
    },

    scrollToLastGroup() {
      const lastGroupIndex = getLastIndex(this.questionGroups)

      const HALF_SECOND = 500

      setTimeout(() => {
        const newGroupDiv = getFirstItem(this.$refs?.[`group-${lastGroupIndex}`])

        /*
         ** Eslint is expecting an assignment here
         ** but we are 100% sure what we want is an function call
         ** to the element we have a reference to. Because refs can
         ** be 'undefined', we have added conditional access to it to be safe
         */

        // eslint-disable-next-line no-unused-expressions
        newGroupDiv?.scrollIntoView({ behavior: 'smooth' })
      }, HALF_SECOND)
    },

    async addTemplate() {
      window.sessionStorage.setItem('infoCreateTemplate', JSON.stringify(this.categories))
      await this.saveSurvey()
      await this.$router.push('/templates')
    },

    saveBinding(e) {
      const sKey = 83
      if (e.altKey && e.keyCode === sKey) {
        e.preventDefault()
        this.saveSurvey()
      }
    },

    curateAdvancedSettings() {
      const groups = this.questionGroups.map(group => ({
        ...group,

        questions: group.questions.map(question => {
          const {
            facialcoding: _,
            enforce_facialcoding: __,
            ...advancedOptions
          } = question.advancedOptions

          const nullRemovedAdvancedOptions = Object.entries(advancedOptions).filter(
            ([, value]) => value !== null,
          )

          return {
            ...question,
            advancedOptions: Object.fromEntries(nullRemovedAdvancedOptions),
          }
        }),
      }))

      this.setQuestionGroups(groups)
    },

    curateGroupAdvancedSettings() {
      const groups = this.questionGroups.map(group => {
        if (!group.advancedOptions) {
          return {
            ...group,
            advancedOptions: {},
          }
        }

        const nullRemovedAdvancedOptions = Object.entries(group.advancedOptions).filter(
          ([, value]) => value !== null,
        )

        return {
          ...group,
          advancedOptions: Object.fromEntries(nullRemovedAdvancedOptions),
        }
      })

      this.setQuestionGroups(groups)
    },
  },

  mounted() {
    window.addEventListener('keydown', this.saveBinding)

    if (this.questions_groups.length) {
      this.setQuestionGroups(this.questions_groups)
    } else {
      this.questionGroups.push(this.getDefaultGroup())
    }
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.saveBinding)
  },

  created() {
    this.loadSurvey()
    window.scrollTo({ top: 0 })
  },
}
</script>

<style lang="scss" scoped>
.container {
  padding: 0 10px;
}

.scheme {
  height: 100%;
}
.all-surveys {
  margin-bottom: 25px;
}
.upload {
  .scheme-info {
    padding: 30px 0 20px;
    h1 {
      @include tipography(26);
      font-weight: bold;
    }
    div {
      margin-top: 10px;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      p {
        @include tipography(16);
        margin: 3px 0 0 15px;
        @include d(s) {
          margin: 10px 0 0;
        }
      }
    }
    > p {
      @include tipography(14);
      color: $gray-2;
      span {
        color: $white;
      }
    }
    .light & {
      h1,
      p,
      span {
        color: $gray-6;
      }
      > p {
        color: $gray;
      }
    }
  }
}
.all-surveys {
  display: flex;
  align-items: center;
  margin-top: 15px;
  @include tipography(14);
  &::before {
    content: '';
    display: block;
    width: 15px;
    height: 15px;
    background: url(../../assets/imgs/back.svg) no-repeat center;
    margin-right: 5px;
  }
  .light & {
    color: $gray-4;
    &::before {
      background: url(../../assets/imgs/back_dark.svg) no-repeat center;
    }
  }
}
.scheme-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  padding: 28px 0px;
  border-top: 1px solid $white;
  .title {
    h2 {
      @include tipography(26);
      text-transform: capitalize;
      margin-bottom: 10px;
      white-space: normal;
      word-break: break-all;
    }
    button {
      display: flex;
      align-items: center;
      padding: 5px 20px;
      background: none;
      border: 2px solid $blue;
      border-radius: 3px;
      cursor: pointer;
      @include tipography(12);
      text-transform: uppercase;
      letter-spacing: 1px;
      white-space: nowrap;
      transition: all 0.3 ease;
      margin-top: 15px;
      @include d(s) {
        margin-left: 0;
        margin: 20px 5px 0px 0px;
      }
      &::before {
        content: '';
        display: block;
        width: 15px;
        height: 15px;
        background: url(../../assets/imgs/back.svg) no-repeat center;
        margin-right: 5px;
      }
      &:hover {
        transform: scale(1.01);
      }
      &:focus {
        outline: none;
      }
    }
  }
  .light & {
    border-top: 1px solid $gray-5;
    .title {
      h2 {
        color: $main;
      }
      button {
        color: $gray-6;
        &::before {
          background: url(../../assets/imgs/back_dark.svg) no-repeat center;
        }
      }
    }
  }
}
.scheme-btns {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  @include d(s) {
    flex-wrap: wrap;
  }
  img {
    cursor: pointer;
  }
  button,
  a {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px 20px;
    margin-left: 20px;
    background: none;
    border: 2px solid $blue;
    border-radius: 3px;
    cursor: pointer;
    @include tipography(12);
    text-transform: uppercase;
    letter-spacing: 1px;
    white-space: nowrap;
    transition: all 0.3 ease;
    height: 30px;
    @include d(s) {
      margin-left: 0;
      margin: 20px 5px 0px 0px;
    }
    &:hover {
      transform: scale(1.01);
    }
    &:focus {
      outline: none;
    }
  }
  button {
    background: $blue;
    color: $white;
  }
  .light & {
    a {
      color: $gray-6;
    }
  }
}

.form {
  position: relative;
  display: flex;
  align-items: center;
}
.confirmUpload {
  display: flex;
  align-items: center;
  position: absolute;
  width: 200px;
  bottom: -40px;
  left: -25px;
  display: flex;
  animation: growDown 400ms ease-in-out forwards;
  transform-origin: top center;
  p {
    @include tipography(14);
    color: $gray-6;
  }
  button {
    padding: 5px;
    @include tipography(10);
    &:first-of-type {
      margin-right: 10px;
      border: none;
    }
    &:last-of-type {
      margin-right: 10px;
      background: $blue;
      color: $white;
    }
  }
}
#media {
  @include tipography(12);
  margin-top: 0;
  background: $gray-light;
  padding: 7px 10px;
  border-radius: 3px;
  color: $gray-6;
  text-transform: uppercase;
  &::before {
    background: url(../../assets/imgs/media_icon.svg) no-repeat center;
    background-size: contain;
  }
}
.buttons {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 10px;
  button {
    background: $green;
    border: 2px solid $green;
    @include tipography(16);
    border-radius: 3px;
    padding: 5px 10px;
    margin-right: 20px;
    text-transform: uppercase;
    letter-spacing: 1px;
    white-space: nowrap;
    font-size: 0.75em;
    cursor: pointer;
    transition: all 0.3 ease;
    &:focus {
      outline: none;
    }
    &:hover {
      transform: scale(1.01);
    }
    @include d(s) {
      margin: 20px 5px 0px 0px;
    }
    &:nth-of-type(3) {
      padding: 2.5px 10px;
      @include flexMiddle;

      &::before {
        content: '';
        display: block;
        width: 20px;
        height: 20px;
        margin-right: 8px;
        background: url(../../assets/imgs/settings.svg) no-repeat center;
        background-size: contain;
      }
    }
  }
}
.scheme-panel {
  position: relative;
  a {
    img {
      width: 25px;
      height: 25px;
    }
  }
}

@media (max-width: 575.98px) {
  .upload {
    .scheme-info {
      h1 {
        @include tipography(24);
        font-weight: bold;
      }
    }
  }

  .form {
    position: relative;
    display: flex;
    align-items: center;
    margin-top: 20px;
    button {
      margin: 0 45px 0 0;
    }
    a {
      margin: 0 35px 0 0;
    }
  }
}
</style>
